import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  RemoveCircleOutline as RemoveIcon,
  Inventory as BundleIcon,
  Lock as LockIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import i18n from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';
import { catalogErrorText } from './bookCatalogErrors';

const BUNDLE_API = `${config.API_BASE_URL}/api/v1/content/admin/book-bundles`;
const BOOK_API = `${config.API_BASE_URL}/api/v1/content/admin/books`;
const TEAM_API = `${config.API_BASE_URL}/em/v1/admin/teams`;

const defaultForm = {
  title: '',
  description: '',
  price: '',
  thumbnailUrl: '',
  isActive: true,
  items: [],
  eligibleTeams: ['all'],
};

const emptyItem = { bookId: '', quantity: 1 };

// For the loading functions the mount effect calls: the hook's `t` is for rendering.
const tBundles = (key, options) => i18n.t(key, { ns: 'bookBundles', ...options });

const BookBundleManagementPage = () => {
  const { t } = useTranslation('bookBundles');
  const { getAccessToken } = useAuth();

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error(tBundles('errors.authRequired'));
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessToken]);

  const [bundles, setBundles] = useState([]);
  const [books, setBooks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [saving, setSaving] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);

  // Upload a bundle thumbnail to S3 (same endpoint books, events and content use) and keep the
  // returned URL as thumbnailUrl. The URL field stays editable for pasting an existing link.
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be picked again after a failure
    if (!file) return;
    setUploadingThumb(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error(t('errors.authRequired'));
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'bundle-thumbnails');
      const res = await fetch(`${config.API_BASE_URL}/em/v1/admin/images/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }, // no Content-Type: the browser sets the boundary
        body: formData,
      });
      if (!res.ok) throw new Error(t('errors.uploadFailed'));
      const json = await res.json();
      const imageUrl = json?.data?.imageUrl;
      if (!imageUrl) throw new Error(t('errors.noImageUrl'));
      setForm((prev) => ({ ...prev, thumbnailUrl: imageUrl }));
      toast.success(t('toast.thumbnailUploaded'));
    } catch (err) {
      toast.error(catalogErrorText(err, t('errors.uploadThumbnailFallback')));
    } finally {
      setUploadingThumb(false);
    }
  };

  // Set by the server once anyone has been charged for this bundle: its books and price are
  // what those members paid for, and the shipping weight quoted from them is what was billed.
  // Everything else about it stays editable.
  const booksFrozen = Boolean(selectedBundle?.booksFrozen);
  const frozenPeriods = selectedBundle?.frozenPeriods || [];

  useEffect(() => {
    fetchBundles();
    fetchBooks();
    fetchTeams();
  }, []);

  const fetchBundles = async () => {
    setLoading(true);
    try {
      const res = await fetch(BUNDLE_API, { headers: await authHeaders() });
      if (!res.ok) throw new Error(tBundles('errors.fetchBundles'));
      const data = await res.json();
      setBundles(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(catalogErrorText(err, tBundles('errors.fetchBundles')));
    } finally {
      setLoading(false);
    }
  };

  const fetchBooks = async () => {
    try {
      const res = await fetch(BOOK_API, { headers: await authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch books');
      const data = await res.json();
      setBooks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching books:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await fetch(TEAM_API, { headers: await authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch teams');
      const json = await res.json();
      const list = Array.isArray(json) ? json : (Array.isArray(json.data) ? json.data : []);
      setTeams(list);
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const handleTeamSelection = (e) => {
    const { value, checked } = e.target;
    setForm((prev) => {
      if (value === 'all') {
        return { ...prev, eligibleTeams: checked ? ['all'] : [] };
      }
      const filtered = prev.eligibleTeams.filter((t) => t !== 'all');
      return {
        ...prev,
        eligibleTeams: checked ? [...filtered, value] : filtered.filter((t) => t !== value),
      };
    });
  };

  const openCreate = () => {
    setIsEdit(false);
    setSelectedBundle(null);
    setForm({ ...defaultForm, items: [{ ...emptyItem }] });
    setDialogOpen(true);
  };

  const openEdit = (bundle) => {
    setIsEdit(true);
    setSelectedBundle(bundle);
    const items =
      bundle.items && bundle.items.length > 0
        ? bundle.items.map((item) => ({
            bookId: item.bookId || item.book?.id || '',
            quantity: item.quantity ?? 1,
          }))
        : [{ ...emptyItem }];
    setForm({
      title: bundle.title || '',
      description: bundle.description || '',
      price: bundle.price ?? '',
      thumbnailUrl: bundle.thumbnailUrl || '',
      isActive: bundle.isActive ?? true,
      items,
      eligibleTeams: bundle.eligibleTeams || ['all'],
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error(t('errors.titleRequired'));
      return;
    }
    if (form.price === '' || isNaN(Number(form.price)) || Number(form.price) < 0) {
      toast.error(t('errors.priceRequired'));
      return;
    }
    const validItems = form.items.filter((i) => i.bookId);
    if (validItems.length === 0) {
      toast.error(t('errors.bookRequired'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        price: parseFloat(form.price),
        thumbnailUrl: form.thumbnailUrl || null,
        isActive: form.isActive,
        items: validItems.map((i) => ({
          bookId: i.bookId,
          quantity: parseInt(i.quantity, 10) || 1,
        })),
        eligibleTeams: form.eligibleTeams.length > 0 ? form.eligibleTeams : ['all'],
      };
      const url = isEdit ? `${BUNDLE_API}/${selectedBundle.id}` : BUNDLE_API;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: await authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          translateServerMessage(err.message, t(isEdit ? 'errors.updateFailed' : 'errors.createFailed'))
        );
      }
      toast.success(t(isEdit ? 'toast.updated' : 'toast.created'));
      setDialogOpen(false);
      fetchBundles();
    } catch (err) {
      toast.error(catalogErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bundle) => {
    if (!window.confirm(t('confirmDelete', { title: bundle.title }))) return;
    try {
      const res = await fetch(`${BUNDLE_API}/${bundle.id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      if (!res.ok) throw new Error(t('errors.deleteFailed'));
      toast.success(t('toast.deleted'));
      fetchBundles();
    } catch (err) {
      toast.error(catalogErrorText(err, t('errors.deleteFallback')));
    }
  };

  // Item row helpers
  const updateItem = (index, field, value) => {
    const updated = [...form.items];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, items: updated });
  };

  const addItemRow = () => {
    setForm({ ...form, items: [...form.items, { ...emptyItem }] });
  };

  const removeItemRow = (index) => {
    const updated = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: updated.length > 0 ? updated : [{ ...emptyItem }] });
  };

  const getBookTitle = (bookId) => {
    const book = books.find((b) => b.id === bookId);
    return book ? book.title : bookId;
  };

  const getItemCount = (bundle) => {
    if (bundle.items) return bundle.items.length;
    if (bundle.itemCount != null) return bundle.itemCount;
    return 0;
  };

  const filteredBundles = bundles.filter((b) =>
    !search || b.title?.toLowerCase().includes(search.toLowerCase())
  );

  // Books already picked in other rows (to discourage duplicates in the dropdown)
  const usedBookIds = (excludeIndex) =>
    form.items
      .filter((_, i) => i !== excludeIndex)
      .map((i) => i.bookId)
      .filter(Boolean);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{t('header.title')}</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {t('header.subtitle')}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title={t('header.refresh')}>
            <IconButton onClick={fetchBundles} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {t('header.create')}
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('search.placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.title')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.price')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.items')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.teams')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.status')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBundles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box py={4}>
                      <BundleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">{t('table.empty')}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBundles.map((bundle) => (
                  <TableRow key={bundle.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        {bundle.thumbnailUrl ? (
                          <img
                            src={bundle.thumbnailUrl}
                            alt={bundle.title}
                            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <Box sx={{ width: 48, height: 48, borderRadius: 1.5, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BundleIcon color="action" />
                          </Box>
                        )}
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <Typography variant="subtitle2" fontWeight={600}>{bundle.title}</Typography>
                            {bundle.booksFrozen && (
                              <Tooltip
                                title={t('table.frozenTooltip', { periods: (bundle.frozenPeriods || []).join(', ') })}
                                slotProps={{ transition: { timeout: 0 } }}>
                                <LockIcon fontSize="inherit" color="action" />
                              </Tooltip>
                            )}
                          </Box>
                          {bundle.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {bundle.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        ${Number(bundle.price || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={t('table.bookCount', { count: getItemCount(bundle) })} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {bundle.eligibleTeams?.includes('all') ? t('table.allTeams') : (bundle.eligibleTeams?.join(', ') || t('table.allTeams'))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bundle.isActive ? t('status.active') : t('status.inactive')}
                        color={bundle.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('table.edit')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(bundle)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('table.delete')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(bundle)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEdit ? t('dialog.editTitle') : t('dialog.createTitle')}
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            {booksFrozen && (
              <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 2 }}>
                <Trans
                  t={t}
                  i18nKey="dialog.frozenNotice"
                  values={{ periods: frozenPeriods.join(', ') }}
                  components={{ strong: <strong /> }}
                />
              </Alert>
            )}
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('dialog.title')}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('dialog.description')}
                  multiline
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('dialog.price')}
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  disabled={booksFrozen}
                  helperText={booksFrozen ? t('dialog.priceLocked') : ' '}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><MoneyIcon /></InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label={t('dialog.thumbnailUrl')}
                    placeholder={t('dialog.thumbnailPlaceholder')}
                    value={form.thumbnailUrl}
                    onChange={(e) => setForm({ ...form, thumbnailUrl: e.target.value })}
                  />
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={uploadingThumb ? <CircularProgress size={16} /> : <UploadIcon />}
                    disabled={uploadingThumb}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap', height: 56, flexShrink: 0 }}
                  >
                    {uploadingThumb ? t('dialog.uploading') : t('dialog.upload')}
                    <input hidden type="file" accept="image/*" onChange={handleThumbnailUpload} />
                  </Button>
                  {form.thumbnailUrl ? (
                    <Box
                      component="img"
                      key={form.thumbnailUrl}
                      src={form.thumbnailUrl}
                      alt={t('dialog.thumbnailPreview')}
                      sx={{
                        width: 56, height: 56, objectFit: 'cover', borderRadius: 1,
                        border: '1px solid', borderColor: 'divider', flexShrink: 0,
                      }}
                      onError={(ev) => { ev.target.style.display = 'none'; }}
                    />
                  ) : null}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('dialog.eligibleTeams')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value="all"
                        checked={form.eligibleTeams.includes('all')}
                        onChange={handleTeamSelection}
                      />
                    }
                    label={t('dialog.allTeams')}
                  />
                  {teams.map((team) => (
                    <FormControlLabel
                      key={team.id || team.name}
                      control={
                        <Checkbox
                          value={team.name}
                          checked={form.eligibleTeams.includes(team.name)}
                          onChange={handleTeamSelection}
                          disabled={form.eligibleTeams.includes('all')}
                        />
                      }
                      label={team.name}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label={form.isActive ? t('status.active') : t('status.inactive')}
                />
              </Grid>

              {/* Bundle Items */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('dialog.items')}
                    {booksFrozen && (
                      <Chip size="small" icon={<LockIcon />} label={t('dialog.locked')} sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addItemRow}
                    disabled={booksFrozen}>
                    {t('dialog.addBook')}
                  </Button>
                </Box>

                {form.items.map((item, index) => {
                  const used = usedBookIds(index);
                  return (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap={2}
                      mb={1.5}
                    >
                      <FormControl sx={{ flex: 3 }} size="small">
                        <InputLabel>{t('dialog.book')}</InputLabel>
                        <Select
                          value={item.bookId}
                          onChange={(e) => updateItem(index, 'bookId', e.target.value)}
                          label={t('dialog.book')}
                          disabled={booksFrozen}
                        >
                          <MenuItem value="">
                            <em>{t('dialog.selectBook')}</em>
                          </MenuItem>
                          {books.map((book) => (
                            <MenuItem
                              key={book.id}
                              value={book.id}
                              disabled={used.includes(book.id)}
                            >
                              {book.title}{' '}
                              {book.price != null && (
                                <Typography component="span" variant="caption" color="text.secondary">
                                  &nbsp;— ${Number(book.price).toFixed(2)}
                                </Typography>
                              )}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label={t('dialog.quantity')}
                        type="number"
                        size="small"
                        sx={{ flex: 1, minWidth: 80 }}
                        value={item.quantity}
                        onChange={(e) =>
                          updateItem(index, 'quantity', Math.max(1, parseInt(e.target.value, 10) || 1))
                        }
                        disabled={booksFrozen}
                        inputProps={{ min: 1 }}
                      />
                      <Tooltip title={t('dialog.remove')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeItemRow(index)}
                          disabled={booksFrozen || (form.items.length === 1 && !item.bookId)}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setDialogOpen(false)}>{t('dialog.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? t('dialog.saving') : isEdit ? t('dialog.update') : t('dialog.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookBundleManagementPage;
